import React , { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBell, faEnvelope, faSearch, faBars, faHome, faUser, faUsers, faBookmark, faBriefcase, faCoffee, faCalendarDay, faUsd, faMagic, faVideoCamera, faBicycle, faGear, faRightFromBracket, faCircleQuestion } from '@fortawesome/free-solid-svg-icons'
import { brandLogo, BRAND_LOGO_ALT } from '../branding'
import profilePictureContImage from '../Images/defprofile.png'

// firebase
import { db } from '../firebase';
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import { collection, getDocs, limit, query, where } from "firebase/firestore";

// routers
import { useNavigate, Link } from "react-router-dom";
import { logAdminActivity } from '../utils/activityLogger';
import { normalizeAdminType, resolveAdminRecord } from '../utils/adminAccess';

// mobile navbar
import Container from 'react-bootstrap/Container';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import Offcanvas from 'react-bootstrap/Offcanvas';

function Dashnav() {
  const [userProfilePicture, setProfilePicture] = useState(profilePictureContImage);
  const [allUsers, setAllUsers] = useState([]);
  const [results, setResults] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const lastSearchLogRef = useRef('');
  const searchLogTimeoutRef = useRef(null);
  const [showMenu, setShowMenu] = useState(false);
  const [showNotifMenu, setShowNotifMenu] = useState(false);
  const [showMessageMenu, setShowMessageMenu] = useState(false);
  const searchResultsRef = useRef(null);
  const profileMenuRef = useRef(null);
  const notifMenuRef = useRef(null);
  const messageMenuRef = useRef(null);
  const userSearchLoadPromiseRef = useRef(null);
  const [applications, setApplications] = useState([]);
  const [viewedStudentIds, setViewedStudentIds] = useState(new Set());
  const [overdueStudents, setOverdueStudents] = useState([]);
  const [adminRole, setAdminRole] = useState('admin');
  const [userSearchLoaded, setUserSearchLoaded] = useState(false);
  const [notificationsLoaded, setNotificationsLoaded] = useState(false);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [overdueLoaded, setOverdueLoaded] = useState(false);
  const [overdueLoading, setOverdueLoading] = useState(false);

  const auth = getAuth();
  let navigate = useNavigate();
  const isSuperAdmin = adminRole === 'superAdmin';

  function goToProfile() {
    navigate("/profile");
  }

  useEffect(() => {
    let cancelled = false;
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setAdminRole('admin');
        setProfilePicture(profilePictureContImage);
        return;
      }

      try {
        const adminRecord = await resolveAdminRecord(user);
        if (!cancelled) {
          setAdminRole(normalizeAdminType(adminRecord?.adminType));
          if (adminRecord?.profilePic) {
            setProfilePicture(adminRecord.profilePic);
          }
        }

        if (!adminRecord?.profilePic) {
          const profileQuery = query(collection(db, "users"), where("userId", "==", user.uid), limit(1));
          const profileSnapshot = await getDocs(profileQuery);
          const profilePic = profileSnapshot.docs[0]?.data()?.profilePic || profilePictureContImage;
          if (!cancelled) {
            setProfilePicture(profilePic);
          }
        }
      } catch (error) {
        console.error('Failed to load admin role for dash nav:', error);
        if (!cancelled) {
          setAdminRole('admin');
          setProfilePicture(profilePictureContImage);
        }
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [auth]);

  const loadUserSearchIndex = useCallback(async () => {
    if (userSearchLoaded) {
      return allUsers;
    }

    if (userSearchLoadPromiseRef.current) {
      return userSearchLoadPromiseRef.current;
    }

    userSearchLoadPromiseRef.current = getDocs(query(collection(db, "users"), limit(600)))
      .then((querySnapshot) => {
        const users = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          users.push({
            profilePic: data.profilePic || profilePictureContImage,
            bio: data.bio || "",
            userStatus: data.userStatus || "",
            userId: data.userId || doc.id,
          });
        });
        setAllUsers(users);
        setUserSearchLoaded(true);
        return users;
      })
      .catch((error) => {
        console.error('Failed to load dashboard search index:', error);
        return [];
      })
      .finally(() => {
        userSearchLoadPromiseRef.current = null;
      });

    return userSearchLoadPromiseRef.current;
  }, [allUsers, userSearchLoaded]);

  // close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchResultsRef.current && !searchResultsRef.current.contains(event.target)) {
        setResults([]);
      }
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
      if (notifMenuRef.current && !notifMenuRef.current.contains(event.target)) {
        setShowNotifMenu(false);
      }
      if (messageMenuRef.current && !messageMenuRef.current.contains(event.target)) {
        setShowMessageMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchNewApplications = useCallback(async () => {
    if (notificationsLoading) return;

    setNotificationsLoading(true);
    try {
      const usersQuery = query(collection(db, "users"), where("userStatus", "==", "inactive"));
      const activityQuery = query(
        collection(db, "AdminActivity"),
        where("action", "==", "Viewed student phone number"),
        limit(1000)
      );
      const [usersSnap, activitySnap] = await Promise.all([
        getDocs(usersQuery),
        getDocs(activityQuery)
      ]);
      const items = [];
      const ids = new Set();

      usersSnap.forEach((doc) => {
        const data = doc.data();
        items.push({
          id: doc.id,
          userId: data.userId || doc.id,
          admissionNumber: data.admissionNumber || '',
          bio: data.bio || "New applicant",
          course: data.course || "Course pending",
          appDate: data.appDate || null,
          profilePic: data.profilePic || profilePictureContImage
        });
      });

      activitySnap.forEach((doc) => {
        const data = doc.data();
        if (data.studentId) {
          ids.add(String(data.studentId));
        }
        const match = String(data.details || '').match(/\(([^)]+)\)/);
        if (match?.[1]) {
          ids.add(match[1]);
        }
      });

      setApplications(items);
      setViewedStudentIds(ids);
      setNotificationsLoaded(true);
    } catch (error) {
      console.error('Failed to load new application notifications:', error);
    } finally {
      setNotificationsLoading(false);
    }
  }, [notificationsLoading]);

  const fetchOverdueStudents = useCallback(async () => {
    if (overdueLoading) return;

    setOverdueLoading(true);
    try {
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();

      const [courseSnap, usersSnap, incomeSnap] = await Promise.all([
        getDocs(collection(db, 'courses')),
        getDocs(query(collection(db, 'users'), where('userStatus', '==', 'Student'))),
        getDocs(collection(db, 'Income'))
      ]);
      const courseMap = {};
      const incomeByStudent = {};

      courseSnap.forEach((doc) => {
        const data = doc.data();
        courseMap[data.mcourseName] = {
          price: parseFloat(data.mcoursePrice || 0),
          installment: parseFloat(data.feeInstallments || 0),
        };
      });

      incomeSnap.forEach((doc) => {
        const data = doc.data();
        const amount = parseFloat(data.incomeAmount || 0);
        const studentId = data.studentId;
        const paymentDate = data.paymentDate?.toDate?.();
        if (!studentId) return;
        if (!incomeByStudent[studentId]) incomeByStudent[studentId] = [];
        incomeByStudent[studentId].push({ amount, date: paymentDate });
      });

      const overdueList = [];
      usersSnap.docs.forEach((doc) => {
        const student = { id: doc.id, ...doc.data() };
        const userId = student.userId;
        const courseName = student.course;
        const course = courseMap[courseName];
        if (!course) return;
        const admissionRaw = student.admissionDate?.toDate?.() || student.admissionDate;
        const admissionDate = admissionRaw ? new Date(admissionRaw) : null;
        if (!admissionDate || Number.isNaN(admissionDate.getTime())) return;

        const dueDay = admissionDate.getDate();
        const isPastDueDay = now.getDate() > dueDay;
        const incomes = incomeByStudent[userId] || [];
        let totalPaid = 0;
        let paidThisMonth = 0;

        incomes.forEach((income) => {
          totalPaid += income.amount;
          if (
            income.date &&
            income.date.getMonth() === currentMonth &&
            income.date.getFullYear() === currentYear
          ) {
            paidThisMonth += income.amount;
          }
        });

        const balance = course.price - totalPaid;
        const monthlyInstallment = course.installment;
        if (balance <= 0 || monthlyInstallment <= 0 || !isPastDueDay) return;

        const amountDue = Math.min(Math.max(monthlyInstallment - paidThisMonth, 0), balance);
        if (amountDue <= 0) return;

        overdueList.push({
          id: student.id,
          userId,
          name: student.bio || 'Student',
          course: courseName || 'Course',
          profilePic: student.profilePic || profilePictureContImage,
          amountDue,
          dueDay
        });
      });

      overdueList.sort((a, b) => b.amountDue - a.amountDue);
      setOverdueStudents(overdueList);
      setOverdueLoaded(true);
    } catch (error) {
      console.error('Failed to load overdue students:', error);
    } finally {
      setOverdueLoading(false);
    }
  }, [overdueLoading]);

  useEffect(() => {
    let cancelled = false;
    const search = searchValue.trim().toLowerCase();

    if (search.length < 2) {
      setResults([]);
      return undefined;
    }

    loadUserSearchIndex().then((users) => {
      if (cancelled) return;
      const filtered = users
        .filter((user) => String(user.bio || '').toLowerCase().includes(search))
        .slice(0, 12);
      setResults(filtered);
    });

    return () => {
      cancelled = true;
    };
  }, [searchValue, loadUserSearchIndex]);

  useEffect(() => {
    if (!showMessageMenu) return undefined;

    if (!overdueLoaded) {
      fetchOverdueStudents();
    }

    const interval = window.setInterval(fetchOverdueStudents, 120000);
    return () => window.clearInterval(interval);
  }, [fetchOverdueStudents, overdueLoaded, showMessageMenu]);

  const { newApplications, newApplicationsCount } = useMemo(() => {
    const filtered = applications
      .filter((app) => {
        const candidateIds = [app.userId, app.id, app.admissionNumber]
          .filter(Boolean)
          .map((value) => String(value));
        const phoneViewed = candidateIds.some((id) => viewedStudentIds.has(id));
        return !phoneViewed;
      })
      .sort((a, b) => {
        const aTime = a.appDate?.seconds ? a.appDate.seconds : 0;
        const bTime = b.appDate?.seconds ? b.appDate.seconds : 0;
        return bTime - aTime;
      });
    return {
      newApplications: filtered.slice(0, 20),
      newApplicationsCount: filtered.length
    };
  }, [applications, viewedStudentIds]);

  const formatAppDate = (value) => {
    if (!value) return 'Date pending';
    const dateObj = value.toDate ? value.toDate() : new Date(value);
    if (Number.isNaN(dateObj.getTime())) return 'Date pending';
    return dateObj.toLocaleDateString();
  };

  // search handler
  const handleKeyUp = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchValue(value);

    if (searchLogTimeoutRef.current) {
      clearTimeout(searchLogTimeoutRef.current);
    }
    searchLogTimeoutRef.current = setTimeout(() => {
      const trimmed = value.trim();
      if (trimmed === lastSearchLogRef.current) return;
      lastSearchLogRef.current = trimmed;
      if (trimmed) {
        logAdminActivity({
          action: 'Search',
          details: `Dashboard search: "${trimmed}"`,
          meta: { page: 'Dashboard Search', query: trimmed }
        });
      }
    }, 600);

    if (value.trim().length < 2) {
      setResults([]);
    }
  };

  function viewStudent(userId) {
    navigate('/view-student', { state: { myData: userId } });
  }

  const handleToggleMessageMenu = () => {
    const nextOpen = !showMessageMenu;
    setShowMessageMenu(nextOpen);
    if (nextOpen && !overdueLoaded) {
      fetchOverdueStudents();
    }
  };

  const handleToggleNotifMenu = () => {
    const nextOpen = !showNotifMenu;
    setShowNotifMenu(nextOpen);
    if (nextOpen && !notificationsLoaded) {
      fetchNewApplications();
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className='dashNav'>
      <div className='dashMenuIcon'>
        {[false].map((expand) => (
          <Navbar key={expand} bg="" className="" expand={expand}>
            <Container fluid>
              <Navbar.Toggle aria-controls={`offcanvasNavbar-expand-${expand}`} />
              <Navbar.Offcanvas
                id={`offcanvasNavbar-expand-${expand}`}
                placement="start"
              >
                <Offcanvas.Header closeButton>
                  <Offcanvas.Title>
                    <img src={brandLogo} alt={BRAND_LOGO_ALT} className='dashnavLogo' />
                  </Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                  <Nav className="justify-content-end flex-grow-1 pe-3">
                    <div className='leftLinkCont d-flex align-items-center'>
                      <Link to="/dashboard" className='leftNavLinks '>
                        <FontAwesomeIcon icon={faHome} className="laftNavIcon"/>
                        Dashboard
                      </Link>
                    </div>
                    {/* Your other links remain the same */}
                  </Nav>
                </Offcanvas.Body>
              </Navbar.Offcanvas>
            </Container>
          </Navbar>
        ))}
      </div>

      <div className='searchDashCont'>
        <input placeholder='Search for a student' onKeyUp={handleKeyUp}/>
        <FontAwesomeIcon icon={faSearch} className="dashSearchIcon "/>
      </div>

      {results.length > 0 && (
        <div className='searchResults' ref={searchResultsRef}>
          {results.map((result, index) => (
            <div key={index} className='resultItem' onClick={() => viewStudent(result.userId)}>
              <div className='resultItemDiv'>
                <img src={result.profilePic} alt={`${result.bio}'s profile`} /> 
              </div>
              <div>
                <p>{result.bio}</p>
                <p className='statusResult'>{result.userStatus}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className='allNotifCont'>
        <div className='chatCont' ref={messageMenuRef}>
          <button className='notifButton' type="button" onClick={handleToggleMessageMenu}>
            <FontAwesomeIcon icon={faEnvelope} className="dashNoticIcon "/>
            <p className='notifCounter'>{overdueStudents.length}</p>
          </button>
          {showMessageMenu && (
            <div className='notifDropdown messageDropdown'>
              <div className='notifDropdownHeader'>
                <h6>Overdue Fees</h6>
                <span>{overdueStudents.length}</span>
              </div>
              {overdueLoading && (
                <p className='notifEmpty'>Loading overdue fees...</p>
              )}
              {!overdueLoading && overdueStudents.length === 0 && (
                <p className='notifEmpty'>No overdue students right now.</p>
              )}
              {overdueStudents.slice(0, 20).map((student) => (
                <button
                  key={student.userId || student.id}
                  type="button"
                  className='notifItem'
                  onClick={() => {
                    setShowMessageMenu(false);
                    viewStudent(student.userId);
                  }}
                >
                  <img src={student.profilePic} alt="" />
                  <div>
                    <p className='notifName'>{student.name}</p>
                    <p className='notifMeta'>{student.course} • KES {student.amountDue.toLocaleString()} due</p>
                  </div>
                  <span className='notifAction'>View</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className='NotifCont' ref={notifMenuRef}>
          <button className='notifButton' type="button" onClick={handleToggleNotifMenu}>
            <FontAwesomeIcon icon={faBell} className="dashNoticIcon "/>
            <p className='notifCounter2'>{newApplicationsCount}</p>
          </button>
          {showNotifMenu && (
            <div className='notifDropdown'>
              <div className='notifDropdownHeader'>
                <h6>New Applications</h6>
                <span>{newApplicationsCount}</span>
              </div>
              {notificationsLoading && (
                <p className='notifEmpty'>Loading applications...</p>
              )}
              {!notificationsLoading && newApplicationsCount === 0 && (
                <p className='notifEmpty'>No new applications right now.</p>
              )}
              {newApplications.map((item) => (
                <button
                  key={item.userId}
                  type="button"
                  className='notifItem'
                  onClick={() => {
                    setShowNotifMenu(false);
                    viewStudent(item.userId);
                  }}
                >
                  <img src={item.profilePic} alt="" />
                  <div>
                    <p className='notifName'>{item.bio}</p>
                    <p className='notifMeta'>{item.course} • {formatAppDate(item.appDate)}</p>
                  </div>
                  <span className='notifAction'>View</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className='profileMenuWrap' ref={profileMenuRef}>
          <button className='profCont profileMenuButton' onClick={() => setShowMenu((prev) => !prev)}>
            <img src={userProfilePicture} alt="" />
          </button>
          {showMenu && (
            <div className='profileDropdown'>
              <button className='profileDropdownItem' onClick={() => { setShowMenu(false); goToProfile(); }}>
                <FontAwesomeIcon icon={faUser} /> Profile
              </button>
              {isSuperAdmin && (
                <button className='profileDropdownItem' onClick={() => { setShowMenu(false); navigate('/settings'); }}>
                  <FontAwesomeIcon icon={faGear} /> Settings
                </button>
              )}
              <button className='profileDropdownItem' onClick={() => { setShowMenu(false); navigate('/help-support'); }}>
                <FontAwesomeIcon icon={faCircleQuestion} /> Help & Support
              </button>
              <div className='profileDropdownDivider'></div>
              <button className='profileDropdownItem profileDropdownDanger' onClick={handleLogout}>
                <FontAwesomeIcon icon={faRightFromBracket} /> Logout
              </button>
            </div>
          )}
        </div>
      </div>        
    </div>
  )
}

export default Dashnav
